import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  faChartLine,
  faCheck,
  faClock,
  faCreditCard,
  faHeadset,
  faMobileScreenButton,
  faPercent,
  faQrcode,
  faRobot,
  faShieldHalved,
  faStore,
  faTruckFast,
  faUsers,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import LandingYoutubeFrame from "../../../components/LandPage/LandingYoutubeFrame"
import { useGetLandingVideos } from "../../../hooks/Landing/useGetLandingVideos"
import { extractYoutubeId } from "../../../Objects/Generics/Helps"
import { getLandingAnimationDelay } from "../landingPageUtils"

type CardContent = { title: string; description: string }

type DigitalMenuConversionSectionsProps = {
  onCreateAccount: (target: string) => void
  onLeadFocus: (target: string) => void
  onViewExamples: (target: string) => void
}

const featureIcons = [faQrcode, faTruckFast, faCreditCard, faRobot, faChartLine, faHeadset]

const valueIcons = [faStore, faClock, faUsers, faPercent, faShieldHalved, faMobileScreenButton]

const landingAnimationDuration = "1.25s"

export default function DigitalMenuConversionSections({
  onCreateAccount,
  onLeadFocus,
  onViewExamples,
}: DigitalMenuConversionSectionsProps) {
  const { t } = useTranslation("seoLanding")

  const pains = t("digital_menu.pains", { returnObjects: true }) as CardContent[]

  const solutionSteps = t("digital_menu.solution_steps", { returnObjects: true }) as CardContent[]

  const conversionFeatures = t("digital_menu.conversion_features", { returnObjects: true }) as CardContent[]

  const implementationOptions = t("digital_menu.implementation_options", { returnObjects: true }) as CardContent[]

  const valueStack = t("digital_menu.value_stack", { returnObjects: true }) as CardContent[]

  const objections = t("digital_menu.objections", { returnObjects: true }) as CardContent[]

  const beforeAfter = t("digital_menu.before_after", { returnObjects: true }) as CardContent[]

  const { handleGetLandingVideos } = useGetLandingVideos()

  const [publicMenuYoutubeUrl, setPublicMenuYoutubeUrl] = useState<string | null>(null)

  const publicMenuVideoId = extractYoutubeId(publicMenuYoutubeUrl ?? "")

  useEffect(() => {
    handleGetLandingVideos()
      .then((value) => setPublicMenuYoutubeUrl(value.publicMenuYoutubeUrl))
      .catch(() => setPublicMenuYoutubeUrl(null))
  }, [handleGetLandingVideos])

  return (
    <>
      <section className="bg-slate-50 py-14">
        <div className="mx-auto max-w-[1280px] px-6">
          <Animate type={AnimationTypes.fadeInTraslate} duration={landingAnimationDuration}>
            <div className="mx-auto max-w-3xl text-center">
              <span className="text-xs font-black uppercase tracking-wide text-red-600">
                {t("digital_menu.pains_badge")}
              </span>
              <h2 className="mt-2 text-3xl font-black text-slate-950">{t("digital_menu.pains_title")}</h2>
              <p className="mt-4 text-justify text-base leading-relaxed text-slate-600">
                {t("digital_menu.pains_description")}
              </p>
            </div>
          </Animate>

          <div className="mt-7 grid gap-4 md:grid-cols-3">
            {pains.map((pain, index) => (
              <Animate
                key={pain.title}
                type={AnimationTypes.slideUpIn}
                duration="1.2s"
                delay={getLandingAnimationDelay(index)}
              >
                <article className="rounded-xl border border-red-100 bg-white p-5 shadow-sm">
                  <div className="mx-auto mb-4 flex h-9 w-9 items-center justify-center rounded-full bg-red-50 text-sm font-black text-red-600">
                    {index + 1}
                  </div>
                  <h3 className="text-center text-lg font-black text-slate-900">{pain.title}</h3>
                  <p className="mt-3 text-justify text-sm leading-relaxed text-slate-600">{pain.description}</p>
                </article>
              </Animate>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="mx-auto grid max-w-[1280px] gap-8 px-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <Animate type={AnimationTypes.slideRightIn} duration={landingAnimationDuration}>
            <div className="flex flex-col items-center">
              <span className="text-xs font-black uppercase tracking-wide text-emerald-700">
                {t("digital_menu.solution_badge")}
              </span>
              <h2 className="mt-2 text-center text-3xl font-black text-slate-950">
                {t("digital_menu.solution_title")}
              </h2>
              <p className="mt-4 text-justify text-base leading-relaxed text-slate-600">
                {t("digital_menu.solution_description")}
              </p>

              <Animate type={AnimationTypes.zoomIn} duration="1.15s" delay="0.15s">
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <button
                    onClick={() => onLeadFocus("solution_section_setup")}
                    className="inline-flex cursor-pointer items-center justify-center rounded-xl bg-slate-950 px-5 py-3 text-sm font-black text-white transition hover:bg-slate-800"
                    data-interaction-description-key="interactionDescriptions:pages.landPage.components.digitalMenuConversionSections.button.onLeadFocusSolutionSectionSetup"
                  >
                    {t("digital_menu.cta_done_for_you_short")}
                  </button>
                </div>
              </Animate>
            </div>
          </Animate>

          <div className="grid gap-4 md:grid-cols-3">
            {solutionSteps.map((step, index) => (
              <Animate
                key={step.title}
                type={AnimationTypes.slideUpIn}
                duration="1.2s"
                delay={getLandingAnimationDelay(index)}
              >
                <article className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-5 shadow-sm">
                  <div className="mx-auto mb-4 flex h-9 w-9 items-center justify-center rounded-full bg-emerald-700 text-sm font-black text-white">
                    {index + 1}
                  </div>
                  <h3 className="text-center font-black text-slate-900">{step.title}</h3>
                  <p className="mt-3 text-justify text-sm leading-relaxed text-slate-600">{step.description}</p>
                </article>
              </Animate>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-950 py-14 text-white">
        <div className="mx-auto max-w-[1280px] px-6">
          <Animate type={AnimationTypes.fadeInTraslate} duration={landingAnimationDuration}>
            <div className="mx-auto max-w-3xl text-center">
              <span className="text-xs font-black uppercase tracking-wide text-orange-300">
                {t("digital_menu.features_badge")}
              </span>
              <h2 className="mt-2 text-3xl font-black">{t("digital_menu.features_title")}</h2>
              <p className="mt-4 text-justify text-sm leading-relaxed text-slate-300">
                {t("digital_menu.features_description")}
              </p>
            </div>
          </Animate>

          <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {conversionFeatures.map((feature, index) => (
              <Animate
                key={feature.title}
                type={AnimationTypes.zoomIn}
                duration="1.18s"
                delay={getLandingAnimationDelay(index)}
              >
                <article className="rounded-xl border border-white/10 bg-white/5 p-5">
                  <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-white text-orange-600">
                    <FontAwesomeIcon icon={featureIcons[index] ?? faMobileScreenButton} />
                  </div>
                  <h3 className="text-center font-black">{feature.title}</h3>
                  <p className="mt-3 text-justify text-sm leading-relaxed text-slate-300">{feature.description}</p>
                </article>
              </Animate>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-14">
        <div className="mx-auto max-w-[1280px] px-6">
          <Animate type={AnimationTypes.fadeInTraslate} duration={landingAnimationDuration}>
            <div className="mx-auto max-w-3xl text-center">
              <span className="text-xs font-black uppercase tracking-wide text-red-600">
                {t("digital_menu.value_badge")}
              </span>
              <h2 className="mt-2 text-3xl font-black text-slate-950">{t("digital_menu.value_title")}</h2>
              <p className="mt-4 text-justify text-sm leading-relaxed text-slate-600">
                {t("digital_menu.value_description")}
              </p>
            </div>
          </Animate>

          <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {valueStack.map((item, index) => (
              <Animate
                key={item.title}
                type={AnimationTypes.fadeInTraslate}
                duration="1.18s"
                delay={getLandingAnimationDelay(index)}
              >
                <article className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                  <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-white text-red-600 shadow-sm">
                    <FontAwesomeIcon icon={valueIcons[index] ?? faCheck} />
                  </div>
                  <h3 className="text-center font-black text-slate-900">{item.title}</h3>
                  <p className="mt-3 text-justify text-sm leading-relaxed text-slate-600">{item.description}</p>
                </article>
              </Animate>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-orange-50 py-14">
        <div className="mx-auto max-w-[1280px] px-6">
          <Animate type={AnimationTypes.fadeInTraslate} duration={landingAnimationDuration}>
            <h2 className="mx-auto max-w-3xl text-center text-3xl font-black text-slate-950">
              {t("digital_menu.before_after_title")}
            </h2>
          </Animate>

          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            {beforeAfter.map((item, index) => (
              <Animate
                key={item.title}
                type={index === 0 ? AnimationTypes.slideRightIn : AnimationTypes.slideLeftIn}
                duration="1.25s"
                delay={getLandingAnimationDelay(index)}
              >
                <article
                  className={`rounded-2xl border p-6 shadow-sm ${
                    index === 0 ? "border-red-200 bg-white" : "border-emerald-200 bg-emerald-50"
                  }`}
                >
                  <span
                    className={`block text-center text-xs font-black uppercase tracking-wide ${
                      index === 0 ? "text-red-600" : "text-emerald-700"
                    }`}
                  >
                    {index === 0 ? t("digital_menu.before_badge") : t("digital_menu.after_badge")}
                  </span>
                  <h3 className="mt-2 text-center text-2xl font-black text-slate-950">{item.title}</h3>
                  <p className="mt-3 text-justify text-sm leading-relaxed text-slate-700">{item.description}</p>
                </article>
              </Animate>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="mx-auto max-w-[1280px] px-6">
          <Animate type={AnimationTypes.fadeInTraslate} duration={landingAnimationDuration}>
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-3xl font-black text-slate-950">{t("digital_menu.implementation_title")}</h2>
              <p className="mt-4 text-justify text-sm leading-relaxed text-slate-600">
                {t("digital_menu.implementation_description")}
              </p>
            </div>
          </Animate>

          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            {implementationOptions.map((option, index) => (
              <Animate
                key={option.title}
                type={AnimationTypes.zoomIn}
                duration="1.22s"
                delay={getLandingAnimationDelay(index)}
              >
                <article
                  className={`rounded-2xl border p-6 text-center shadow-sm ${
                    index === 0 ? "border-emerald-200 bg-emerald-50" : "border-sky-200 bg-sky-50"
                  }`}
                >
                  <h3 className="text-2xl font-black text-slate-950">{option.title}</h3>
                  <p className="mt-3 text-justify text-sm leading-relaxed text-slate-700">{option.description}</p>
                  <button
                    onClick={() =>
                      index === 0
                        ? onLeadFocus("implementation_done_for_you")
                        : onCreateAccount("implementation_self_service")
                    }
                    className={`mt-5 inline-flex w-full cursor-pointer items-center justify-center rounded-xl px-5 py-3 text-sm font-black transition sm:w-auto ${
                      index === 0
                        ? "bg-emerald-700 text-white hover:bg-emerald-800"
                        : "bg-gradient-to-r from-sky-600 to-indigo-600 text-white shadow-lg shadow-sky-500/20 hover:from-sky-700 hover:to-indigo-700"
                    }`}
                    data-interaction-description-key={
                      index === 0
                        ? "interactionDescriptions:pages.landPage.components.digitalMenuConversionSections.button.requestConfiguredMenu"
                        : "interactionDescriptions:pages.landPage.components.digitalMenuConversionSections.button.createSelfServiceAccount"
                    }
                  >
                    {index === 0 ? t("digital_menu.cta_done_for_you_short") : t("digital_menu.cta_create_short")}
                  </button>
                </article>
              </Animate>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-14">
        <div className="mx-auto max-w-[1280px] px-6">
          <Animate type={AnimationTypes.fadeInTraslate} duration={landingAnimationDuration}>
            <div className="mx-auto max-w-3xl text-center">
              <span className="text-xs font-black uppercase tracking-wide text-orange-700">
                {t("digital_menu.objections_badge")}
              </span>
              <h2 className="mt-2 text-3xl font-black text-slate-950">{t("digital_menu.objections_title")}</h2>
              <p className="mt-4 text-justify text-sm leading-relaxed text-slate-600">
                {t("digital_menu.objections_description")}
              </p>
            </div>
          </Animate>

          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {objections.map((item) => (
              <Animate key={item.title} type={AnimationTypes.slideUpIn} duration="1.18s">
                <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h3 className="text-center font-black text-slate-900">{item.title}</h3>
                  <p className="mt-3 text-justify text-sm leading-relaxed text-slate-600">{item.description}</p>
                </article>
              </Animate>
            ))}
          </div>

          <Animate type={AnimationTypes.zoomIn} duration="1.25s" delay="0.16s">
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <button
                onClick={() => onLeadFocus("objections_done_for_you")}
                className="inline-flex cursor-pointer items-center justify-center rounded-xl bg-gradient-to-r from-red-600 to-orange-500 px-5 py-3 text-sm font-black text-white shadow-lg shadow-red-500/20 transition hover:scale-[1.01]"
                data-interaction-description-key="interactionDescriptions:pages.landPage.components.digitalMenuConversionSections.button.onLeadFocusObjectionsDoneForYou"
              >
                {t("digital_menu.cta_done_for_you_short")}
              </button>

              <button
                onClick={() => onCreateAccount("objections_self_service")}
                className="inline-flex cursor-pointer items-center justify-center rounded-xl bg-gradient-to-r from-sky-600 to-indigo-600 px-5 py-3 text-sm font-black text-white shadow-lg shadow-sky-500/20 transition hover:from-sky-700 hover:to-indigo-700"
                data-interaction-description-key="interactionDescriptions:pages.landPage.components.digitalMenuConversionSections.button.onCreateAccountObjectionsSelfService"
              >
                {t("digital_menu.cta_create_short")}
              </button>
            </div>
          </Animate>
        </div>
      </section>

      <section className="bg-white py-12">
        <Animate type={AnimationTypes.fadeInTraslate} duration={landingAnimationDuration}>
          <div className="mx-auto max-w-3xl px-6 text-center">
            <span className="text-xs font-black uppercase tracking-wide text-slate-500">
              {t("digital_menu.examples_badge")}
            </span>
            <h2 className="mt-2 text-3xl font-black text-slate-950">{t("digital_menu.examples_title")}</h2>
            <p className="mt-4 text-justify text-sm leading-relaxed text-slate-600">
              {t("digital_menu.examples_description")}
            </p>
            <button
              type="button"
              onClick={() => onViewExamples("bottom_examples")}
              className="mt-6 inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-black text-slate-800 transition hover:bg-slate-50 sm:w-auto"
              data-interaction-description-key="interactionDescriptions:pages.landPage.components.digitalMenuConversionSections.button.onViewExamplesBottomExamples"
            >
              <FontAwesomeIcon icon={faMobileScreenButton} />
              {t("digital_menu.cta_example")}
            </button>
          </div>
        </Animate>
      </section>

      {publicMenuVideoId && (
        <Animate type={AnimationTypes.zoomIn} duration="1.3s">
          <section className="mx-auto max-w-[1280px] px-6 pb-10">
            <LandingYoutubeFrame
              youtubeUrl={publicMenuYoutubeUrl}
              title={t("digital_menu.carousel_title")}
              aspectRatio="9 / 19.5"
              frameWrapperClassName="max-w-[320px] mx-auto"
            />
          </section>
        </Animate>
      )}
    </>
  )
}
